import React, { useState, useEffect } from "react";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { FileText, Eye } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useGetFieldForceTransactions } from "@features/wx/fieldForce/hooks";
import { useParams } from "react-router-dom";
import { getStatusColor } from "@features/wx/utils";

interface TransactionData {
  id: string;
  name: string;
  status: "overdue" | "approved" | "completed" | "in-progress";
  created: string;
  assigned: string;
  expected: string;
  completed: string;
}

export const TransactionsTab: React.FC = () => {
  const { id, ff_no } = useParams<{ id: string; ff_no: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(PAGE_SIZE);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  const { data: transactions } = useGetFieldForceTransactions(
    ff_no,
    currentPage
  );

  // Update pagination state when data changes
  useEffect(() => {
    const totalItems = transactions?.count || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    setPagination({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [transactions, currentPage, itemsPerPage]);

  // Helper function for transaction status badge
  const getTransactionStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            Approved
          </Badge>
        );
      case "overdue":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            Overdue
          </Badge>
        );
      case "completed":
        return (
          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
            Completed
          </Badge>
        );
      case "in-progress":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            In-Progress
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handlePageChange = async (page: number) => {
    if (page !== currentPage && page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  const handleViewTransaction = (row: TransactionData) => {
    // Navigate to transaction view or open modal
  };

  const columns: TableColumn<TransactionData>[] = [
    {
      key: "serviceNo",
      header: "SOP ID",
      sortable: true,
    },
    {
      key: "sopAssociationTypeDisplay",
      header: "Type",
      sortable: true,
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge" as const,
      sortable: true,
      badgeConfig: {
        getClassName: (value: string) => getStatusColor(value),
      },
    },
    {
      key: "assignDate",
      header: "Assigned",
      sortable: true,
      responsive: { hidden: "md:table-cell" },
    },
    {
      key: "expectedEndDate",
      header: "Expected",
      sortable: true,
      responsive: { hidden: "lg:table-cell" },
    },
    {
      key: "completedDate",
      header: "Completed",
      sortable: true,
      responsive: { hidden: "lg:table-cell" },
    },
  ];

  const actions = [
    {
      label: "View",
      onClick: handleViewTransaction,
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
  ];

  return (
    <div className=" space-y-6">
      <h3 className="card-heading-text mb-4 flex items-center">
        <FileText className="mr-2 h-5 w-5 text-blue-500" />
        All Service Orders
      </h3>

      <div className="mt-2">
        <GenericTable
          data={transactions?.results || []}
          columns={columns}
          rowKey="id"
          selectable={false}
          actions={actions}
          actionsType="dropdown"
          getRowClassName={(row) =>
            row.status === "overdue"
              ? "bg-red-50"
              : row.status === "in-progress"
              ? "bg-yellow-50"
              : row.status === "completed"
              ? "bg-green-50"
              : ""
          }
          emptyMessage="No service orders found"
          loading={false}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
